import StencilHTMLElement from "./stencil-element";

/**
 * QuickView component for displaying product details in a modal.
 * 
 * @see {@link file://./../extensions/quick-view.js}
 * @see {@link file://./../../sections/quick-view.liquid}
 * @see {@link file://./../../../snippets/modal-quick-view.liquid}
 */
class QuickView extends StencilHTMLElement {
  static get observedAttributes() { return ["handle"]; }

  name = "QuickView";

  #abort = null;
  #root = null;

  constructor() {
    super();
  }

  connectedCallback() {
    // Inject into first <div> inside the element, otherwise the element itself
    this.#root = this.querySelector("div") || this;

    // Initial load if markup already provided a handle
    const handle = this.getAttribute("handle");
    if (handle) this.#load(handle);

    this.classList.add("qv-ready");
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "handle" && newValue && newValue !== oldValue) {
      this.#load(newValue);
    }
  }

  open(handle, { sectionId } = {}) {
    if (sectionId) this.setAttribute("section-id", sectionId);
    this.setAttribute("handle", handle);
  }

  async #load(handle) {
    if (!this.#root) return;

    // Cancel any in-flight fetch
    if (this.#abort) this.#abort.abort();
    this.#abort = new AbortController();

    const sectionId = this.getAttribute("section-id") || "product-main";
    const url = `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`;

    // Loading UI
    this.#root.innerHTML = "";
    this.setAttribute("aria-busy", "true");
    this.classList.add("qv-loading");

    try {
      const res = await fetch(url, {
        method: "GET",
        credentials: "same-origin",
        signal: this.#abort.signal,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const html = await res.text();
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      this.#root.innerHTML = parsedHtml.querySelector('.shopify-section').innerHTML;

      this.dispatchEvent(new CustomEvent("quick-view:loaded", {
        bubbles: true,
        detail: { handle, sectionId, url },
      }));
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(this.logPrefix, "load failed:", err);
        this.#root.innerHTML = `<div class="qv-error" role="alert">Sorry, failed to load this product.</div>`;
      }
    } finally {
      this.classList.remove("qv-loading");
      this.setAttribute("aria-busy", "false");
      this.#abort = null;
    }
  }
}

export default QuickView;